import React, { useMemo, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../CommonComponents/OPL/Popover/Popover';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Separator } from '../../../CommonComponents/OPL/Separator/Separator';
import { Checkbox } from '../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Tooltip } from '../../../CommonComponents/OPL/Tooltip/Tooltip';
import { MAX_COLUMNS_COUNT } from './ColumnsPopover.meta';

interface Props {
  isOpen: boolean;
  defaultColumns: string[];
  onOpenChange: (isOpen: boolean) => void;
  onApply: (columns: string[]) => void;
  columnGroups: Record<string, string[]>;
}

export const ColumnsPopover = (props: Props) => {
  const { isOpen, onOpenChange } = props;

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger>
        <IconButton
          aria-label="Select report columns"
          icon="fal fa-columns"
          showCaret
          variant="secondary"
          size="large"
          css="width: 70px"
        />
      </PopoverTrigger>

      {isOpen && <Content {...props} />}
    </Popover>
  );
};

const Content = ({ defaultColumns, columnGroups, onApply }: Props) => {
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [searchTerm, setSearchTerm] = useState('');

  const groups = useMemo(() => {
    return Object.entries(columnGroups)
      .map(([groupName, columns]) => ({
        groupName,
        columns: columns.filter(column => column.toLowerCase().includes(searchTerm.toLowerCase())),
      }))
      .filter(group => group.columns.length > 0);
  }, [columnGroups, searchTerm]);

  const isSelectionValid =
    selectedColumns.length > 0 && selectedColumns.length <= MAX_COLUMNS_COUNT;

  return (
    <PopoverContent width={292}>
      <Box as="section" p="16">
        <Input
          value={searchTerm}
          onChange={evt => setSearchTerm(evt.target.value)}
          leftIcon="far fa-search"
          placeholder="Search"
          aria-label="Search columns"
        />
      </Box>

      <Separator />

      {groups.length > 0 ? (
        <form
          aria-label="Select report columns"
          onSubmit={evt => {
            evt.preventDefault();

            if (!isSelectionValid) {
              return;
            }

            onApply(selectedColumns);
          }}
        >
          <Box as="section" maxHeight="40vh" overflowY="auto">
            {groups.map(({ groupName, columns }) => (
              <React.Fragment key={groupName}>
                <Text as="p" key={groupName} variant="smallCaps" px="16" pt="12" pb={4}>
                  {groupName}
                </Text>

                {columns.map(column => (
                  <Box key={column} px="16" py={6}>
                    <Checkbox
                      checked={selectedColumns.includes(column)}
                      onChange={() => {
                        if (selectedColumns.includes(column)) {
                          setSelectedColumns(selectedColumns.filter(c => c !== column));
                        } else {
                          setSelectedColumns([...selectedColumns, column]);
                        }
                      }}
                    >
                      {column}
                    </Checkbox>
                  </Box>
                ))}
              </React.Fragment>
            ))}
          </Box>

          <Separator />

          <Box as="section" display="flex" alignItems="center" p="16">
            <Button
              variant="link"
              disabled={selectedColumns.length === 0}
              onClick={() => setSelectedColumns([])}
              size="small"
              mr="auto"
            >
              Unselect All
            </Button>

            <Tooltip
              disabled={isSelectionValid}
              content={
                selectedColumns.length === 0
                  ? 'Select at least one item from the menu'
                  : selectedColumns.length > MAX_COLUMNS_COUNT
                  ? `Only ${MAX_COLUMNS_COUNT} columns can be added to a report`
                  : ''
              }
              placement="top"
            >
              <span>
                <Button type="submit" disabled={!isSelectionValid}>
                  Apply
                </Button>
              </span>
            </Tooltip>
          </Box>
        </form>
      ) : (
        <Text as="p" fontWeight={500} p="16">
          No results found
        </Text>
      )}
    </PopoverContent>
  );
};
